"use client";

import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import StatusBadge from "@/components/dashboard/StatusBadge";
import { User, Puzzle, Bell, CreditCard, Plus, Copy, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { getTeamMembers, getSessionUser, getBilling } from "@/services/api";
import type { TeamMember, BillingInfo } from "@/services/api";

function formatAmount(amount: number): string {
  return `€${amount.toFixed(2)}`;
}

function roleDisplayLabel(role: string): string {
  return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
}

const apiKeys = [
  { name: "Production API Key", key: "r365_prod_ak_...7f3d", created: "2025-01-10" },
  { name: "Staging API Key", key: "r365_stg_ak_...2e1a", created: "2025-02-01" },
];

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState("profile");
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamMembersLoading, setTeamMembersLoading] = useState(true);
  const [entraTenantId, setEntraTenantId] = useState<string | null>(null);
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    getBilling().then(setBilling).catch(() => setBilling(null));
  }, []);

  useEffect(() => {
    getTeamMembers()
      .then((res) => setTeamMembers(res.members))
      .catch(() => setTeamMembers([]))
      .finally(() => setTeamMembersLoading(false));
  }, []);

  useEffect(() => {
    getSessionUser().then((user) => setEntraTenantId(user?.entraTenantId ?? null));
  }, []);

  const tabs = [
    { id: "profile", label: t("settings.profileTeam"), icon: User },
    { id: "integrations", label: t("settings.integrations"), icon: Puzzle },
    { id: "notifications", label: t("settings.notifications"), icon: Bell },
    { id: "plan", label: t("settings.planBilling"), icon: CreditCard },
  ];

  const notifications = [
    { label: t("settings.syncCompleted"), desc: t("settings.syncCompletedDesc") },
    { label: t("settings.syncFailed"), desc: t("settings.syncFailedDesc") },
    { label: t("settings.storageThreshold"), desc: t("settings.storageThresholdDesc") },
    { label: t("settings.weeklyDigest"), desc: t("settings.weeklyDigestDesc") },
    { label: t("settings.securityAlerts"), desc: t("settings.securityAlertsDesc") },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">{t("settings.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("settings.subtitle")}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <nav className="flex lg:flex-col gap-1 lg:w-52 shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors text-left",
                activeTab === tab.id
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-muted-foreground hover:bg-secondary"
              )}
            >
              <tab.icon className="h-4 w-4 shrink-0" />
              <span className="hidden lg:inline">{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="flex-1 space-y-6">
          {activeTab === "profile" && (
            <div className="rounded-lg border border-border/50 bg-card p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium">{t("settings.teamMembers")}</h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded">
                    {t("auth.comingSoon")}
                  </span>
                  <Button size="sm" variant="outline" className="gap-1.5" disabled>
                    <Plus className="h-3.5 w-3.5" /> {t("settings.invite")}
                  </Button>
                </div>
              </div>
              <div className="space-y-3">
                {teamMembersLoading && teamMembers.length === 0 && (
                  <p className="text-sm text-muted-foreground py-2">Loading…</p>
                )}
                {!teamMembersLoading && teamMembers.length === 0 && (
                  <p className="text-sm text-muted-foreground py-2">No team members yet.</p>
                )}
                {teamMembers.map((m) => (
                  <div key={m.id} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0">
                    <p className="text-sm font-medium">{m.email ?? "—"}</p>
                    <span className="text-xs bg-secondary px-2 py-0.5 rounded">{roleDisplayLabel(m.role)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "integrations" && (
            <>
              <div className="rounded-lg border border-border/50 bg-card p-5 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium">{t("settings.m365Connection")}</h3>
                  <StatusBadge status="success" label={t("settings.connected")} />
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm max-w-lg">
                  <span className="text-muted-foreground">{t("settings.tenantId")}</span>
                  <span className="font-mono text-xs">{entraTenantId ?? "—"}</span>
                  <span className="text-muted-foreground">{t("settings.lastVerified")}</span>
                  <span>February 10, 2025</span>
                </div>
              </div>

              
            </>
          )}

          {activeTab === "notifications" && (
            <div className="rounded-lg border border-border/50 bg-card p-5 shadow-sm">
              <h3 className="text-sm font-medium mb-4">{t("settings.emailAlerts")}</h3>
              <div className="space-y-4">
                {notifications.map((item) => (
                  <div key={item.label} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{item.label}</p>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                    <Switch />
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "plan" && (
            <div className="rounded-lg border border-border/50 bg-card p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle2 className="h-5 w-5 text-accent" />
                <div>
                  <h3 className="text-sm font-medium">
                    {billing ? billing.planName : (t("billing.enterprisePlan") as string)}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {billing
                      ? `${formatAmount(billing.monthlyPrice)}${t("pricing.perMonth")} ${t("pricing.exclVat")} · ${t("settings.billedMonthly")}`
                      : "—"}
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to="/dashboard/billing">{t("settings.manageBilling")}</Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
